<template>
  <div class="fee-slot-panel">
    <!-- 费用大类标题栏 -->
    <div
      class="category-header"
      :class="{ expanded: isExpanded, required: category.alwaysRequired }"
      @click="toggleExpand"
    >
      <div class="header-left">
        <i :class="category.icon" class="category-icon"></i>
        <span class="category-label">{{ category.label }}</span>
        <el-tag v-if="category.alwaysRequired" size="mini" type="danger" effect="plain">必传</el-tag>
        <el-tag v-else size="mini" type="info" effect="plain">按需</el-tag>
      </div>
      <div class="header-right">
        <!-- 完成度指示器 -->
        <span class="completion-badge" :class="completionClass">
          {{ completedCount }} / {{ totalRequiredCount }}
        </span>
        <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="expand-icon"></i>
      </div>
    </div>

    <!-- 展开后的文件槽卡片网格 -->
    <transition name="slide">
      <div v-show="isExpanded" class="slots-container">
        <div class="slots-grid">
          <div
            v-for="slot in visibleSlots"
            :key="slot.id"
            class="slot-card"
            :class="getSlotStatusClass(slot)"
          >
            <!-- 卡片顶部 -->
            <div class="slot-header">
              <div class="slot-title">
                <span class="status-dot" :class="getSlotStatusClass(slot)"></span>
                <span>{{ slot.label }}</span>
                <span v-if="slot.required" class="required-star">*</span>
              </div>
              <el-tooltip v-if="slot.tip" :content="slot.tip" placement="top" effect="dark">
                <i class="el-icon-question tip-icon"></i>
              </el-tooltip>
            </div>

            <!-- 已上传文件列表 -->
            <div class="file-list">
              <div
                v-for="(file, idx) in getSlotFiles(slot.id)"
                :key="idx"
                class="file-item"
              >
                <div class="file-info" @click="handlePreview(file)">
                  <i :class="getFileIcon(file)" class="file-type-icon"></i>
                  <span class="file-name" :title="file.name || file.fileName || getFileName(file)">
                    {{ file.name || file.fileName || getFileName(file) }}
                  </span>
                </div>
                <div class="file-actions">
                  <el-button
                    v-if="file.url && (file.url.includes('http://') || file.url.includes('https://'))"
                    type="text"
                    size="mini"
                    icon="el-icon-download"
                    @click.stop="downloadFile(file)"
                  ></el-button>
                  <el-button
                    v-if="mode === 'edit' && canEdit"
                    type="text"
                    size="mini"
                    icon="el-icon-delete"
                    class="delete-btn"
                    @click.stop="handleDelete(slot.id, idx)"
                  ></el-button>
                </div>
              </div>
              <div v-if="getSlotFiles(slot.id).length === 0" class="empty-tip">
                <i class="el-icon-folder-add"></i>
                <span>暂无文件</span>
              </div>
            </div>

            <!-- 上传按钮 + 最低文件数提醒 -->
            <div class="slot-footer" v-if="mode === 'edit' && canEdit">
              <el-upload
                class="slot-upload"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :accept="slot.accept"
                :show-file-list="false"
                :on-progress="(e) => onUploadProgress(e, slot.id)"
                :on-success="(res, file) => onUploadSuccess(res, file, slot.id)"
                :on-error="(err) => onUploadError(err, slot.id)"
                :before-upload="(file) => beforeUpload(file, slot)"
                multiple
              >
                <el-button size="mini" type="primary" icon="el-icon-upload2" plain>
                  上传文件
                </el-button>
              </el-upload>
              <span v-if="slot.minCount" class="min-count-tip">
                至少 {{ slot.minCount }} 个
              </span>
            </div>

            <!-- 审核状态灯（仅 view 模式）-->
            <div class="slot-footer" v-if="mode === 'view'">
              <div class="audit-status" :class="getSlotStatusClass(slot)">
                <template v-if="getSlotFiles(slot.id).length > 0">
                  <i class="el-icon-success" v-if="getAuditStatus(slot.id) === 'pass'"></i>
                  <i class="el-icon-warning" v-else-if="getAuditStatus(slot.id) === 'warn'"></i>
                  <i class="el-icon-error" v-else-if="getAuditStatus(slot.id) === 'fail'"></i>
                  <i class="el-icon-loading" v-else-if="getAuditStatus(slot.id) === 'pending'"></i>
                  <span>{{ getAuditStatusText(slot.id) }}</span>
                </template>
                <template v-else>
                  <i class="el-icon-remove-outline"></i>
                  <span>{{ slot.required ? '缺失必填' : '未上传' }}</span>
                </template>
              </div>
            </div>

            <!-- 上传进度条 -->
            <el-progress
              v-if="uploadingSlots[slot.id]"
              :percentage="uploadingSlots[slot.id]"
              :stroke-width="3"
              class="upload-progress"
            ></el-progress>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import baseUrl from '@/request/config'

export default {
  name: 'FeeSlotPanel',
  props: {
    /** 费用大类配置对象（来自 fee-slot-config.js） */
    category: {
      type: Object,
      required: true,
    },
    /** 模式：edit=助教上传, view=官员审核 */
    mode: {
      type: String,
      default: 'edit',
      validator: v => ['edit', 'view'].includes(v),
    },
    /** 是否可编辑（结合状态判断） */
    canEdit: {
      type: Boolean,
      default: true,
    },
    /** 已上传文件数据 { slotId: [{url, name, ...}] } */
    filesMap: {
      type: Object,
      default: () => ({}),
    },
    /** 审核结果 { slotId: 'pass'|'fail'|'warn'|'pending' } */
    auditResults: {
      type: Object,
      default: () => ({}),
    },
    /** 条件参数，如 { meetingMode: 'ONLINE'|'OFFLINE' } */
    conditions: {
      type: Object,
      default: () => ({}),
    },
    /** 是否默认展开 */
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.defaultExpanded || this.category.defaultExpanded || false,
      uploadingSlots: {}, // { slotId: percentage }
    }
  },
  computed: {
    /** 上传接口地址 */
    uploadUrl() {
      return `${baseUrl}/sign/live/doctor/uploadFile`
    },
    /** 上传请求头（带 token） */
    uploadHeaders() {
      try {
        const tokenData = JSON.parse(localStorage.getItem('token'))
        return { token: tokenData ? tokenData.token : '' }
      } catch {
        return {}
      }
    },
    /** 根据条件过滤后可见的文件槽 */
    visibleSlots() {
      return this.category.slots.filter(slot => {
        if (!slot.conditions) return true
        return Object.entries(slot.conditions).every(
          ([key, val]) => this.conditions[key] === val
        )
      })
    },
    /** 必填且可见的槽位总数 */
    totalRequiredCount() {
      return this.visibleSlots.filter(s => s.required).length
    },
    /** 已完成（有文件）的必填槽位数 */
    completedCount() {
      return this.visibleSlots.filter(s => {
        if (!s.required) return false
        const files = this.getSlotFiles(s.id)
        if (s.minCount) return files.length >= s.minCount
        return files.length > 0
      }).length
    },
    /** 完成度样式 */
    completionClass() {
      if (this.totalRequiredCount === 0) return 'complete'
      if (this.completedCount === this.totalRequiredCount) return 'complete'
      if (this.completedCount > 0) return 'partial'
      return 'empty'
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    /** 获取某槽位的文件列表 */
    getSlotFiles(slotId) {
      return this.filesMap[slotId] || []
    },
    /** 从文件URL中提取文件名 */
    getFileName(file) {
      if (typeof file === 'string') {
        const parts = file.split('/')
        return decodeURIComponent(parts[parts.length - 1]) || '未命名'
      }
      return file.name || file.fileName || '未命名'
    },
    /** 获取文件类型图标 */
    getFileIcon(file) {
      const name = (file.name || file.fileName || file.url || file || '').toLowerCase()
      if (/\.(jpg|jpeg|png|bmp|gif)/.test(name)) return 'el-icon-picture-outline'
      if (/\.pdf/.test(name)) return 'el-icon-document'
      if (/\.(xlsx?|csv)/.test(name)) return 'el-icon-tickets'
      if (/\.(doc|docx)/.test(name)) return 'el-icon-document-copy'
      if (/\.(ppt|pptx)/.test(name)) return 'el-icon-data-board'
      if (/\.(zip|rar|7z)/.test(name)) return 'el-icon-folder'
      if (/\.(mp4|avi|mov)/.test(name)) return 'el-icon-video-camera'
      return 'el-icon-document'
    },
    /** 获取槽位状态样式类 */
    getSlotStatusClass(slot) {
      const files = this.getSlotFiles(slot.id)
      if (files.length === 0) {
        return slot.required ? 'status-missing' : 'status-empty'
      }
      const auditStatus = this.getAuditStatus(slot.id)
      if (auditStatus === 'fail') return 'status-fail'
      if (auditStatus === 'warn') return 'status-warn'
      if (auditStatus === 'pass') return 'status-pass'
      return 'status-uploaded'
    },
    /** 获取审核状态 */
    getAuditStatus(slotId) {
      return this.auditResults[slotId] || null
    },
    /** 获取审核状态文字 */
    getAuditStatusText(slotId) {
      const status = this.getAuditStatus(slotId)
      const map = {
        pass: '审核通过',
        fail: '审核不通过',
        warn: '待确认',
        pending: '审核中...',
      }
      return map[status] || '已上传'
    },
    /** 上传前校验 */
    beforeUpload(file, slot) {
      // 检查文件格式
      if (slot.accept) {
        const accepts = slot.accept.split(',').map(s => s.trim().toLowerCase())
        const ext = '.' + file.name.split('.').pop().toLowerCase()
        if (!accepts.includes(ext)) {
          this.$message.error(`不支持的文件格式 "${ext}"，仅允许：${slot.accept}`)
          return false
        }
      }
      // 文件大小限制 50MB
      if (file.size > 50 * 1024 * 1024) {
        this.$message.error('文件大小不能超过 50MB')
        return false
      }
      return true
    },
    /** 上传进度 */
    onUploadProgress(event, slotId) {
      this.$set(this.uploadingSlots, slotId, Math.round(event.percent))
    },
    /** 上传成功 */
    onUploadSuccess(response, file, slotId) {
      this.$delete(this.uploadingSlots, slotId)
      // 构造文件对象
      const fileObj = {
        url: typeof response === 'string' ? response : (response.data || response.url || response),
        name: file.name,
        size: file.size,
      }
      this.$emit('file-added', {
        categoryCode: this.category.code,
        slotId,
        file: fileObj,
      })
    },
    /** 上传失败 */
    onUploadError(err, slotId) {
      this.$delete(this.uploadingSlots, slotId)
      this.$message.error('文件上传失败，请重试')
      console.error('Upload error:', slotId, err)
    },
    /** 删除文件 */
    handleDelete(slotId, fileIdx) {
      this.$confirm('确认删除此文件？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.$emit('file-removed', {
          categoryCode: this.category.code,
          slotId,
          fileIdx,
        })
      }).catch(() => {})
    },
    /** 预览文件 */
    handlePreview(file) {
      const url = file.url || file
      if (!url) return
      this.$emit('file-preview', url)
    },
    /** 下载文件 */
    downloadFile(file) {
      const url = file.url || file
      if (url) window.open(url, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.fee-slot-panel {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8eaed;
  background: #fff;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }
}

/* ===== 类目标题栏 ===== */
.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background: #fafbfc;
  border-bottom: 1px solid transparent;
  transition: all 0.2s;
  user-select: none;

  &.expanded {
    border-bottom-color: #e8eaed;
    background: #f5f7fa;
  }

  &.required {
    border-left: 3px solid #e6a23c;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;

    .category-icon {
      font-size: 18px;
      color: #409eff;
    }

    .category-label {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;

    .completion-badge {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 10px;
      font-weight: 500;

      &.complete {
        background: #f0f9eb;
        color: #67c23a;
      }

      &.partial {
        background: #fdf6ec;
        color: #e6a23c;
      }

      &.empty {
        background: #fef0f0;
        color: #f56c6c;
      }
    }

    .expand-icon {
      color: #909399;
      transition: transform 0.3s;
    }
  }
}

/* ===== 卡槽网格容器 ===== */
.slots-container {
  padding: 16px;
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

/* ===== 单个卡槽卡片 ===== */
.slot-card {
  border: 1px solid #e8eaed;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: #c0c4cc;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }

  &.status-pass {
    border-color: #b3e19d;
    background: #f9fcf7;
  }

  &.status-fail {
    border-color: #fab6b6;
    background: #fef5f5;
  }

  &.status-warn {
    border-color: #f5dab1;
    background: #fefaf4;
  }

  &.status-missing {
    border-color: #fab6b6;
    border-style: dashed;
  }

  &.status-uploaded {
    border-color: #b3d8ff;
    background: #f5faff;
  }
}

.slot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .slot-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    color: #303133;

    .required-star {
      color: #f56c6c;
      font-weight: bold;
    }
  }

  .tip-icon {
    color: #909399;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      color: #409eff;
    }
  }
}

/* ===== 状态小圆点 ===== */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.status-pass { background: #67c23a; }
  &.status-fail { background: #f56c6c; }
  &.status-warn { background: #e6a23c; }
  &.status-missing { background: #f56c6c; opacity: 0.5; }
  &.status-uploaded { background: #409eff; }
  &.status-empty { background: #c0c4cc; }
}

/* ===== 文件列表 ===== */
.file-list {
  min-height: 32px;
  max-height: 120px;
  overflow-y: auto;
  margin-bottom: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f5f7fa;
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;

    .file-type-icon {
      font-size: 14px;
      color: #909399;
      flex-shrink: 0;
    }

    .file-name {
      font-size: 12px;
      color: #606266;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .file-actions {
    flex-shrink: 0;
    display: flex;
    gap: 0;

    .delete-btn {
      color: #f56c6c;
    }
  }
}

.empty-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
  color: #c0c4cc;
  font-size: 12px;

  i {
    font-size: 16px;
  }
}

/* ===== 底部操作区 ===== */
.slot-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f2f3f5;
  padding-top: 8px;

  .min-count-tip {
    font-size: 11px;
    color: #e6a23c;
  }
}

.slot-upload {
  display: inline-block;
}

/* ===== 审核状态 ===== */
.audit-status {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  &.status-pass {
    color: #67c23a;
  }

  &.status-fail {
    color: #f56c6c;
  }

  &.status-warn {
    color: #e6a23c;
  }

  &.status-uploaded {
    color: #409eff;
  }

  &.status-missing {
    color: #909399;
  }

  &.status-empty {
    color: #c0c4cc;
  }
}

/* ===== 上传进度条 ===== */
.upload-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

/* ===== 展开/收起动画 ===== */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}
</style>
